import { SPLIT_METRIC_MEASURES, Periods, MetricDataPoint, SplitMetricMeasure } from 'cio/utils/metrics/common';
import moment from 'moment-timezone';
import { ONE_HOUR_IN_SECONDS, ONE_DAY_IN_SECONDS, ONE_WEEK_IN_SECONDS } from 'cio/utils/time-constants';
import { CampaignType } from 'cio/models/campaign';
import {
  DashboardTestSplitAction,
  DashboardTestSplitMetric,
  DashboardTestNewsletter,
  DashboardTestResult,
} from 'cio/utils/metrics/models/dashboard-tests';
import { TimeRange } from 'cio/utils/time';

type DashboardTestsApiResponse = {
  newsletters: ApiNewsletter[] | null;
  split_actions: ApiSplitAction[] | null;
};

type ApiNewsletter = {
  id: string;
  name: string;
  sent_at: number;
  variation_count: number;
  automatic_winner: boolean;
  send_percentage: number;
  sent_to_remaining_at: number;
  test_duration: number;
  test_metric: string;
};

type ApiSplitAction = {
  id: string;
  campaign_id: string;
  campaign_name: string;
  campaign_type: string;
  name: string;
  action_state: string;
  split_state: string;
  started: number;
  action_ids: string[];
  split_metrics: ApiSplitMetric[];
};

type ApiSplitMetric = {
  id: string;
  action_id: string;
  campaign_id: string;
  template_id: string;
  sent: number[];
  delivered: number[];
  opened: number[];
  clicked: number[];
  converted: number[];
  period: Periods;
};

function processNewsletter(newsletter: ApiNewsletter): DashboardTestNewsletter {
  return {
    id: newsletter.id,
    name: newsletter.name,
    sentAt: newsletter.sent_at,
    variationCount: newsletter.variation_count,
    automaticWinner: newsletter.automatic_winner,
    sendPercentage: newsletter.send_percentage,
    sentToRemainingAt: newsletter.sent_to_remaining_at,
    testDuration: newsletter.test_duration,
    testMetric: newsletter.test_metric,
  };
}

function processSplitAction(item: ApiSplitAction, range: TimeRange): DashboardTestSplitAction {
  let [original, variation] = item.split_metrics.map((m) => processSplitMetric(m, range));

  variation?.processCTBO(original);

  return {
    id: item.id,
    campaignId: item.campaign_id,
    campaignName: item.campaign_name,
    campaignType: item.campaign_type as CampaignType,
    name: item.name,
    actionState: item.action_state,
    splitState: item.split_state,
    started: item.started,
    actionIds: item.action_ids,
    splitMetrics: [original, variation],
  };
}

function processSplitMetric(item: ApiSplitMetric, range: TimeRange): DashboardTestSplitMetric {
  let sentLength = item.sent.length;
  let timestamps: number[] = [];
  let processedData: Record<SplitMetricMeasure, MetricDataPoint[]> = {
    sent: [],
    delivered: [],
    opened: [],
    clicked: [],
    converted: [],
  };

  if (item.period === Periods.HOURS) {
    let endTimestamp = moment.tz('Etc/GMT+4').startOf('hour').unix();

    timestamps = [...new Array(sentLength)].map((_, i) => endTimestamp - i * ONE_HOUR_IN_SECONDS).reverse();
  } else if (item.period === Periods.MONTHS) {
    let endMoment = moment.tz('Etc/GMT+4').startOf('month');

    timestamps = [...new Array(sentLength)]
      .map((_, i) =>
        endMoment
          .clone() // necessary to avoid mutating endMoment object
          .subtract(i, 'month') // months can be 28/29/30/31 days long: Moment magic to always arrive at first day of the month
          .unix(),
      )
      .reverse();
  } else if (item.period === Periods.WEEKS) {
    let endMomentTimestamp = moment.tz('Etc/GMT+4').startOf('week').unix();

    timestamps = [...new Array(sentLength)].map((_, i) => endMomentTimestamp - i * ONE_WEEK_IN_SECONDS).reverse();
  } else {
    // default to "days" period:
    let endTimestamp = moment.tz('Etc/GMT+4').startOf('day').unix();

    timestamps = [...new Array(sentLength)].map((_, i) => endTimestamp - i * ONE_DAY_IN_SECONDS).reverse();
  }

  SPLIT_METRIC_MEASURES.forEach((measure) => {
    let data: number[] = item[measure] ?? [];

    let measureData = timestamps.map((timestamp, i) => ({
      x: timestamp * 1000,
      y: data[i],
    }));

    processedData[measure] = measureData;
  });

  return new DashboardTestSplitMetric({
    ...item,
    range,
    data: processedData,
  });
}

export function deserialize(
  data: DashboardTestsApiResponse,
  range: TimeRange,
  _meta?: { environmentId?: number | string; campaignId?: number | string },
): DashboardTestResult {
  return {
    newsletters: data.newsletters ? data.newsletters.map(processNewsletter) : [],
    splitActions: data.split_actions ? data.split_actions.map((a) => processSplitAction(a, range)) : [],
  };
}
